/**
 * Iter155 — Master Console: Sector Cleanup Wizard.
 *
 * Lists every distinct value in `db.leads.sector` with the count of rows
 * per bucket + heuristic hints ("looks-like-search-query", "junk",
 * "has-digits", "one-off"). Master picks two or more source buckets +
 * types (or picks) a canonical target, hits "Merge" — the backend does
 * an atomic `updateMany` and the list refreshes.
 *
 * Route: /master/sectors  (super_admin only)
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Wand2, RefreshCw, AlertTriangle, ChevronRight, Filter, Trash2,
} from "lucide-react";
import MasterLayout from "./MasterLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HINT_META = {
  "looks-like-search-query": { label: "Search query", color: "#EF4444" },
  "junk":                    { label: "Junk",         color: "#B91C1C" },
  "has-digits":              { label: "Has digits",   color: "#F59E0B" },
  "one-off":                 { label: "1-off",        color: "#94A3B8" },
};

export default function MasterSectorCleanup() {
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [target, setTarget] = useState("");
  const [merging, setMerging] = useState(false);
  const [filter, setFilter] = useState("all"); // all | flagged
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/master/sectors`);
      setSectors(data?.sectors || []);
    } catch {
      toast.error("Falha ao carregar setores");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    let list = sectors;
    if (filter === "flagged") list = list.filter((s) => s.hints.length > 0);
    if (q) {
      const needle = q.toLowerCase();
      list = list.filter((s) => s.sector.toLowerCase().includes(needle));
    }
    return list;
  }, [sectors, filter, q]);

  const toggle = (name) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(name) ? next.delete(name) : next.add(name);
    return next;
  });

  const selectAllFlagged = () => {
    const all = new Set(sectors.filter((s) => s.hints.length > 0).map((s) => s.sector));
    setSelected(all);
  };

  const doMerge = async () => {
    if (selected.size === 0) { toast.error("Selecione ao menos um setor"); return; }
    if (!target.trim()) { toast.error("Informe o setor canônico (destino)"); return; }
    const sources = [...selected];
    const impacted = sectors
      .filter((s) => selected.has(s.sector))
      .reduce((n, s) => n + s.count, 0);
    if (!window.confirm(
      `Mesclar ${sources.length} setores (${impacted} leads) em "${target.trim()}"?\n\n` +
      `Fontes: ${sources.join(", ")}\n\nEsta operação NÃO pode ser desfeita.`,
    )) return;
    setMerging(true);
    try {
      const { data } = await axios.post(`${API}/master/sectors/merge`, {
        sources, target: target.trim(),
      });
      toast.success(`${data.modified} leads mesclados em "${data.target}" (${data.target_count_now} totais)`);
      setSelected(new Set());
      setTarget("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao mesclar");
    } finally {
      setMerging(false);
    }
  };

  return (
    <MasterLayout title="Sector Cleanup Wizard" subtitle="Merge garbage / duplicate sector values across all leads">
      <div className="space-y-4" data-testid="master-sector-cleanup">
        {/* Toolbar */}
        <div className="flex flex-wrap gap-2 items-center rounded-xl p-3 border"
             style={{ background: "rgba(15,8,32,0.6)", borderColor: "rgba(201,162,78,0.18)" }}>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter sector name…"
            className="al-input h-9 text-[12px] w-56" data-testid="master-sector-search" />
          <select value={filter} onChange={(e) => setFilter(e.target.value)}
            className="al-input h-9 text-[12px] w-40" data-testid="master-sector-filter">
            <option value="all">All ({sectors.length})</option>
            <option value="flagged">
              Only flagged ({sectors.filter((s) => s.hints.length > 0).length})
            </option>
          </select>
          <button type="button" onClick={selectAllFlagged}
            data-testid="master-sector-flag-all"
            className="h-9 px-3 rounded-lg text-[11.5px] font-bold border border-[#F59E0B] text-[#B45309] hover:bg-[#F59E0B]/10 inline-flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> Select all flagged
          </button>
          <button type="button" onClick={load} disabled={loading}
            className="h-9 px-3 rounded-lg text-[11.5px] font-bold border border-[var(--al-border)] inline-flex items-center gap-1.5 hover:border-[#C9A24E]">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Reload
          </button>
          <div className="ml-auto text-[11.5px] text-[var(--al-text-muted)]">
            <Filter className="w-3.5 h-3.5 inline mr-1" />
            {selected.size} selected
          </div>
        </div>

        {/* Merge form */}
        <div className="rounded-xl p-4 border"
             style={{ background: "rgba(15,8,32,0.6)", borderColor: "rgba(201,162,78,0.18)" }}
             data-testid="master-sector-merge-form">
          <div className="flex items-center gap-2 mb-2">
            <Wand2 className="w-4 h-4 text-[#C9A24E]" />
            <h3 className="text-[13px] font-bold">Merge selected → canonical target</h3>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <input value={target} onChange={(e) => setTarget(e.target.value)}
              placeholder='e.g. Restaurant'
              className="al-input h-10 text-[13px] flex-1 min-w-[220px]"
              data-testid="master-sector-target" />
            <button type="button" onClick={doMerge} disabled={merging || selected.size === 0 || !target.trim()}
              data-testid="master-sector-merge-go"
              className="h-10 px-5 rounded-lg bg-[#C9A24E] text-[#0A1233] text-[12.5px] font-bold hover:bg-[#B08F3E] inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
              {merging ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
              Merge into &quot;{target.trim() || "…"}&quot;
            </button>
            <button type="button" onClick={() => { setSelected(new Set()); setTarget(""); }}
              disabled={selected.size === 0 && !target}
              className="h-10 px-3 rounded-lg text-[11.5px] border border-[var(--al-border)] inline-flex items-center gap-1.5 disabled:opacity-40">
              <Trash2 className="w-3.5 h-3.5" /> Reset
            </button>
          </div>
          {selected.size > 0 && (
            <div className="mt-3 text-[11.5px] text-[var(--al-text-muted)]" data-testid="master-sector-preview">
              Will affect{" "}
              <strong className="text-[var(--al-text)]">
                {sectors.filter((s) => selected.has(s.sector)).reduce((n, s) => n + s.count, 0)}
              </strong>{" "}
              leads across {selected.size} sectors:{" "}
              <span className="opacity-75">{[...selected].join(" · ")}</span>
            </div>
          )}
        </div>

        {/* Sectors list */}
        <div className="rounded-xl overflow-hidden border" style={{ background: "rgba(15,8,32,0.6)", borderColor: "rgba(201,162,78,0.18)" }}>
          <div className="grid grid-cols-[36px_1fr_100px_180px] px-3 py-2 border-b text-[10.5px] uppercase tracking-widest font-bold"
               style={{ borderColor: "rgba(201,162,78,0.18)", color: "rgba(255,255,255,0.55)" }}>
            <span></span>
            <span>Sector</span>
            <span className="text-right">Leads</span>
            <span>Flags</span>
          </div>
          {loading ? (
            <div className="p-6 flex items-center justify-center text-[var(--al-text-muted)] text-[12px]">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
          ) : visible.length === 0 ? (
            <div className="p-6 text-center text-[12px] text-[var(--al-text-muted)]">No sectors match this filter.</div>
          ) : (
            <div className="max-h-[540px] overflow-y-auto divide-y" style={{ borderColor: "rgba(201,162,78,0.14)" }} data-testid="master-sector-list">
              {visible.map((s) => {
                const checked = selected.has(s.sector);
                return (
                  <label key={s.sector}
                    data-testid={`master-sector-row-${s.sector}`}
                    style={{ color: "#F1F5F9" }}
                    className={`grid grid-cols-[36px_1fr_100px_180px] px-3 py-2 items-center gap-2 text-[12.5px] cursor-pointer hover:bg-white/5 ${checked ? "bg-[#C9A24E]/10" : ""}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggle(s.sector)}
                      className="accent-[#C9A24E]" />
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="truncate" style={{ color: "#F8FAFC" }}>{s.sector}</span>
                      <button type="button"
                        onClick={(e) => { e.preventDefault(); setTarget(s.sector); }}
                        title="Use as target"
                        className="text-[10px] hover:underline shrink-0"
                        style={{ color: "#C9A24E" }}>
                        set as target →
                      </button>
                    </div>
                    <span className="text-right font-bold" style={{ color: "#F8FAFC" }}>{s.count}</span>
                    <div className="flex flex-wrap gap-1">
                      {s.hints.map((h) => {
                        const meta = HINT_META[h] || { label: h, color: "#94A3B8" };
                        return (
                          <span key={h}
                            className="text-[9.5px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider"
                            style={{ background: `${meta.color}22`, color: meta.color }}>
                            {meta.label}
                          </span>
                        );
                      })}
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </MasterLayout>
  );
}
